import React, { useEffect, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ActivityIndicator, Image } from 'react-native';
import { resolveTravelBackgroundSource } from '@cubemeet/ui';
import SafeView from '@cubemeet/ui/components/common/SafeView';
import DatePickerModal from './DatePickerModal'
import { useNavigate } from 'react-router-dom';
import { supabase as supabaseClient } from '@cubemeet/core/services/supabase';
import { useGlobalToast } from '../contexts/ToastContext';

// format a Date to local YYYY-MM-DD (avoid toISOString timezone shifts)
const formatYMD = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

interface Props {
  visible: boolean;
  onClose: () => void;
  cardSnapshot: {
    id?: string;
    title?: string;
    cover_image?: string;
    address?: string;
  } | null;
  onAdded?: (entry: any) => void;
  // 新增：当用于"外部分享"落地时，传入分享内容以走同一套选行程/日期/时间的流程
  externalShare?: {
    title?: string;
    text?: string;
    url?: string;
    imageUrl?: string;
    source?: 'share-target' | 'manual';
    // 若從遊記/評測帖子「加入行程」，可傳入完整 payload 避免重新抓取
    rawPayload?: any;
  } | null;
}

const detectExternalPartner = (website?: string | null, siteName?: string | null): string | null => {
  const normalize = (value?: string | null) => (typeof value === 'string' ? value.trim().toLowerCase() : '');
  const normalizedSite = normalize(siteName);
  const normalizedHost = (() => {
    if (!website || typeof website !== 'string') return '';
    try {
      const href = website.startsWith('http') ? website : `https://${website}`;
      return new URL(href).hostname.trim().toLowerCase();
    } catch {
      return '';
    }
  })();
  const haystack = [normalizedSite, normalizedHost].filter(Boolean);
  const includes = (needle: string) => haystack.some((value) => value.includes(needle));
  if (includes('klook')) return 'klook';
  if (includes('kkday')) return 'kkday';
  if (includes('trip.com') || includes('ctrip')) return 'trip';
  if (includes('booking')) return 'booking';
  if (includes('agoda')) return 'agoda';
  return null;
};

const fallbackFaviconUrl = (favicon?: string | null, website?: string | null): string | null => {
  if (favicon && typeof favicon === 'string' && favicon.trim()) return favicon.trim();
  if (!website || typeof website !== 'string') return null;
  try {
    const href = website.startsWith('http') ? website : `https://${website}`;
    const host = new URL(href).hostname;
    if (!host) return null;
    return `https://www.google.com/s2/favicons?domain=${host}&sz=64`;
  } catch {
    return null;
  }
};

const AddToItineraryModal: React.FC<Props> = ({ visible, onClose, cardSnapshot, onAdded, externalShare }) => {
  const navigate = useNavigate();
  const { showSuccess, showError, showWarning, showInfo } = useGlobalToast();
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState<Array<any>>([]);
  const [groupsLoading, setGroupsLoading] = useState<boolean>(false);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [groupDays, setGroupDays] = useState<Array<{ dayNumber: number; date: string }>>([]);
  const [selectedDayIndex, setSelectedDayIndex] = useState<number>(0);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [datePickerVisible, setDatePickerVisible] = useState<boolean>(false);
  const [datePickerDays, setDatePickerDays] = useState<Array<{ dayNumber: number; date: string }>>([]);
  const [datePickerGroupId, setDatePickerGroupId] = useState<string | null>(null);
  const [timePickerVisible, setTimePickerVisible] = useState<boolean>(false);
  const [timePickerInitial, setTimePickerInitial] = useState<string | null>(null);
  const [timePickerForDate, setTimePickerForDate] = useState<{ date: string; dayNumber: number; groupId: string | null } | null>(null);
  const [timeSelection, setTimeSelection] = useState<string>('09:00');
  const dragState = React.useRef<{ startY: number | null }>({ startY: null });

  // === External meta enrichment ===
  const EXTRACT_META_ENDPOINT = 'https://extract-meta.cubemeet.app/api/extract-meta';
  const fetchWithTimeout = async (resource: RequestInfo, options?: RequestInit & { timeout?: number }) => {
    const { timeout = 6000, ...rest } = options || {};
    const ctrl = new AbortController();
    const id = setTimeout(() => ctrl.abort(), timeout);
    try {
      const res = await fetch(resource, { ...rest, signal: ctrl.signal });
      return res;
    } finally {
      clearTimeout(id);
    }
  };
  const enrichExternalMeta = async (url: string) => {
    try {
      if (!url) return null;
      const api = `${EXTRACT_META_ENDPOINT}?url=${encodeURIComponent(url)}`;
      const res = await fetchWithTimeout(api, { method: 'GET', headers: { 'Accept': 'application/json' }, timeout: 6500 });
      if (!res.ok) return null;
      const json = await res.json();
      return json as { title?: string; description?: string; image?: string; siteName?: string; favicon?: string };
    } catch (e) {
      return null;
    }
  };

  const startDrag = (clientY: number) => {
    dragState.current.startY = clientY;
    const onMove = (ev: PointerEvent) => {
      const dy = (ev.clientY - (dragState.current.startY ?? 0));
      // if the user drags upward by more than 80px -> expand
      if (dy < -80) {
        setIsExpanded(true);
        cleanup();
      }
    };
    const onUp = () => { cleanup(); };
    const cleanup = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      dragState.current.startY = null;
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  useEffect(() => {
    if (!visible) return;
    (async () => {
      try {
        setGroupsLoading(true);
        const authRes: any = await supabaseClient.auth.getUser();
        const currentUser = authRes?.data?.user || null;
        if (!currentUser) return;

        // 查詢用戶創建的行程
        const { data: createdGroups } = await supabaseClient
          .from('groups')
          .select('id,name,start_date,visibility,creator_id,background_image_url')
          .eq('creator_id', currentUser.id)
          .order('created_at', { ascending: false })
          .limit(20);

        // 查詢用戶加入的行程（作為成員）
        const { data: memberOf } = await supabaseClient
          .from('group_members')
          .select('group_id')
          .eq('user_id', currentUser.id);

        const memberGroupIds = (memberOf || []).map((m: any) => m.group_id).filter(Boolean);
        
        let joinedGroups: any[] = [];
        if (memberGroupIds.length > 0) {
          const { data: memberGroups } = await supabaseClient
            .from('groups')
            .select('id,name,start_date,visibility,creator_id,background_image_url')
            .in('id', memberGroupIds)
            .order('created_at', { ascending: false })
            .limit(20);
          joinedGroups = memberGroups || [];
        }

        // 合併並去重（以 id 為準），創建的行程優先顯示
        const allGroups = [...(createdGroups || [])];
        const existingIds = new Set(allGroups.map((g: any) => g.id));
        for (const g of joinedGroups) {
          if (!existingIds.has(g.id)) {
            allGroups.push(g);
          }
        }
        
        setGroups(allGroups);
      } catch (e) {
        console.error('failed to load groups', e);
      } finally {
        setGroupsLoading(false);
      }
    })();
  }, [visible]);

  useEffect(() => {
    if (!selectedGroupId) {
      setGroupDays([]);
      setSelectedDate(null);
      return;
    }
    (async () => {
      try {
        const { data: g } = await supabaseClient
          .from('groups')
          .select('id,start_date,end_date')
          .eq('id', selectedGroupId)
          .single();
        if (!g) return;
        const start = g.start_date ? new Date(g.start_date) : null;
        const end = g.end_date ? new Date(g.end_date) : null;
        const days: Array<{ dayNumber: number; date: string }> = [];
        if (start && end) {
          // normalize times
          start.setHours(0, 0, 0, 0);
          end.setHours(0, 0, 0, 0);
          const diffMs = end.getTime() - start.getTime();
          const diffDays = Math.floor(diffMs / (24 * 3600 * 1000));
          const total = diffDays >= 0 ? diffDays + 1 : 1;
          for (let i = 0; i < total; i++) {
            const d = new Date(start);
            d.setDate(start.getDate() + i);
            days.push({ dayNumber: i + 1, date: formatYMD(d) });
          }
        }
        setGroupDays(days);
        setSelectedDayIndex(0);
        setSelectedDate(days.length ? days[0].date : null);
      } catch (e) {
        console.error('loadGroupDays error', e);
        setGroupDays([]);
      }
    })();
  }, [selectedGroupId]);

  const handleConfirm = async (overrideGroupId?: string | null, overrideDayDate?: string | null, overrideStartTime?: string | null) => {
    setLoading(true);
    try {
      const authRes: any = await supabaseClient.auth.getUser();
      const currentUser = authRes?.data?.user || null;
      if (!currentUser) {
        showWarning && showWarning('未检测到登录，请先登录');
        onClose();
        return;
      }
      // allow caller to pass explicit groupId/dayDate to avoid setState race conditions
      const groupId = typeof overrideGroupId !== 'undefined' ? overrideGroupId : selectedGroupId;
      if (!groupId) {
        showInfo && showInfo('你还没有行程，可前往创建行程页面');
        const go = confirm('你还没有行程，是否前往创建行程页面？');
        if (go) {
          onClose();
          navigate('/travel/create');
        }
        setLoading(false);
        return;
      }
  const dayDate = typeof overrideDayDate !== 'undefined' ? overrideDayDate : (selectedDate || formatYMD(new Date()));
      const dayNumber = groupDays && groupDays.length > 0 ? groupDays[selectedDayIndex]?.dayNumber || null : null;

      const { data: latest } = await supabaseClient
        .from('group_itinerary_entries')
        .select('sort_order')
        .eq('group_id', groupId)
        .eq('day_date', dayDate)
        .order('sort_order', { ascending: false })
        .limit(1);

      const nextSort = latest && latest.length ? (latest[0].sort_order || 0) + 1 : 1;

      // 🔍 检查是否已存在相同的卡片（同一天内）- 只对普通卡片检查，外部分享不检查
      if (!externalShare && cardSnapshot?.id) {
        const { data: existingEntries, error: checkErr } = await supabaseClient
          .from('group_itinerary_entries')
          .select('id, card_id')
          .eq('group_id', groupId)
          .eq('day_date', dayDate);

        if (!checkErr && existingEntries && existingEntries.length > 0) {
          const isDuplicate = existingEntries.some((entry: any) => entry.card_id === cardSnapshot.id);
          
          if (isDuplicate) {
            console.warn('[AddToItineraryModal] 卡片已存在于当天行程中，拒绝添加重复卡片', {
              cardId: cardSnapshot.id,
              title: cardSnapshot.title,
              groupId,
              dayDate,
            });
            showWarning && showWarning('该卡片已在当天行程中，无法重复添加');
            setLoading(false);
            onClose();
            return;
          }
        }
      }

      // 计算写入 payload：
      // 1) 若传入 externalShare，则构造成"外部分享"条目
      // 2) 否则按卡片逻辑读取 cards_cards 作为自定义/卡片条目
      let customPayload: any = null;
      let noteText: string | null = null;
      let p_card_id: string | null = null;

      if (externalShare && (externalShare.title || externalShare.text || externalShare.url || externalShare.rawPayload)) {
        // 若已有完整的 rawPayload（從遊記/評測帖子加入行程），直接使用，不重新抓取
        if (externalShare.rawPayload && typeof externalShare.rawPayload === 'object') {
          const raw = externalShare.rawPayload;
          customPayload = {
            type: 'external',
            title: raw.title || externalShare.title || '外部分享',
            description: raw.description || externalShare.text || null,
            website: raw.website || externalShare.url || null,
            imageUrl: raw.imageUrl || externalShare.imageUrl || null,
            siteName: raw.siteName || null,
            favicon: raw.favicon || null,
            partner: raw.partner || null,
            resolvedFinalUrl: raw.resolvedFinalUrl || null,
            degraded: raw.degraded ?? null,
            metaPairs: raw.metaPairs || undefined,
            // 保留更多原有欄位
            address: raw.address || null,
            phone: raw.phone || null,
            priceRange: raw.priceRange || null,
            rating: raw.rating || null,
            source: { kind: 'web-share', via: externalShare.source || 'post-import', at: new Date().toISOString() },
          };
        } else {
          // 沒有 rawPayload，走原有的 enrichExternalMeta 抓取流程
          const url = externalShare.url?.trim() || '';
          let host: string | undefined; try { if (url) host = new URL(url).hostname; } catch {}
          let meta: any = null;
          try { if (url) meta = await enrichExternalMeta(url); } catch (e) { meta = null; }
          // Build baseline first so even if enrichment fails we still have a proper external payload.
          const resolvedUrl = meta?.resolvedFinalUrl || meta?.resolvedUrl || null;
          const partnerKey = meta?.partner || detectExternalPartner(resolvedUrl || url || null, meta?.siteName || host || null);
          const faviconUrl = fallbackFaviconUrl(meta?.favicon || null, resolvedUrl || url || null);
          const imageUrl = externalShare.imageUrl || meta?.image || null;
          const degradedFlag = typeof meta?.degraded === 'boolean' ? meta.degraded : null;
          customPayload = {
            type: 'external',
            title: externalShare.title || meta?.title || (host ? `来自 ${host}` : '外部分享'),
            description: externalShare.text || meta?.description || null,
            website: url || null,
            imageUrl,
            siteName: meta?.siteName || host || null,
            favicon: faviconUrl,
            partner: partnerKey,
            resolvedFinalUrl: resolvedUrl || null,
            degraded: degradedFlag,
            metaPairs: (() => {
              const list: Array<{ label: string; value: string }> = [];
              if (meta?.siteName) list.push({ label: '来源网站', value: meta.siteName });
              else if (host) list.push({ label: '来源网站', value: host });
              return list.length ? list : undefined;
            })(),
            source: { kind: 'web-share', via: externalShare.source || 'manual', at: new Date().toISOString() },
          };
        }
        noteText = null;
        p_card_id = null;
      } else {
        // 走原卡片路径
        try {
          if (!cardSnapshot?.id) throw new Error('missing card id');
          const { data: cardRow, error: cardErr } = await supabaseClient
            .from('cards_cards')
            .select('base_payload,description')
            .eq('id', cardSnapshot.id)
            .single();
          if (cardErr) {
            console.warn('failed to fetch card base_payload', cardErr);
          }
          customPayload = cardRow?.base_payload ?? {
            title: cardSnapshot?.title,
            cover_image: cardSnapshot?.cover_image,
            address: cardSnapshot?.address,
          };
          noteText = cardRow?.description ?? null;
          p_card_id = cardSnapshot?.id ?? null;
        } catch (e) {
          console.error('fetch card payload error', e);
          // fallback to minimal payload
          customPayload = {
            title: cardSnapshot?.title,
            cover_image: cardSnapshot?.cover_image,
            address: cardSnapshot?.address,
          };
          noteText = null;
          p_card_id = cardSnapshot?.id ?? null;
        }
      }

      // 外部分享优先直接写入表，避免服务端 RPC 覆盖/忽略 external 的自定义 payload；失败再回退到 RPC。
      if (externalShare && customPayload && customPayload.type === 'external') {
        try {
          const row: any = {
            // 不指定id,让数据库自动生成UUID
            group_id: groupId,
            day_date: dayDate,
            day_number: dayNumber,
            sort_order: nextSort,
            item_type: 'custom',
            card_id: null,
            note: null,
            start_time: typeof overrideStartTime !== 'undefined' ? overrideStartTime : null,
            custom_payload: customPayload,
            inline_payload: { title: customPayload.title, type: 'external', ...customPayload },
            creator_id: currentUser?.id || null,
          };
          const { data: inserted, error: insertErr }: any = await supabaseClient
            .from('group_itinerary_entries')
            .insert([row])
            .select()
            .maybeSingle();
          if (!insertErr && inserted) {
            // 若 DB 层清空/改写了 external 的 payload，这里立刻修复一次
            try {
              const cp = inserted?.custom_payload;
              const ip = inserted?.inline_payload;
              const isEmpty = (v: any) => !v || (typeof v === 'object' && Object.keys(v).length === 0);
              if (isEmpty(cp) || cp?.type !== 'external' || !ip || ip?.type !== 'external' || !ip.website) {
                const { data: patched, error: patchErr } = await supabaseClient
                  .from('group_itinerary_entries')
                  .update({
                    item_type: 'custom',
                    custom_payload: customPayload,
                    inline_payload: { title: customPayload.title, type: 'external', ...customPayload },
                  })
                  .eq('id', inserted.id)
                  .select()
                  .maybeSingle();
                if (!patchErr && patched) Object.assign(inserted, patched);
              }
            } catch {}
            showSuccess && showSuccess('已成功添加到行程');
            onAdded && onAdded(inserted);
            onClose();
            return; // 成功后不再走 RPC
          }
          console.warn('[AddToItineraryModal] direct insert external failed, fallback to RPC', insertErr);
        } catch (e) {
          console.warn('[AddToItineraryModal] direct insert external threw, fallback to RPC', e);
        }
      }

      // 直接插入数据库，不使用RPC（避免UUID格式问题）
      try {
        const row: any = {
          // 不指定id,让数据库自动生成UUID
          group_id: groupId,
          day_date: dayDate,
          day_number: dayNumber,
          sort_order: nextSort,
          item_type: p_card_id ? 'card' : 'custom',
          card_id: p_card_id,
          note: noteText,
          start_time: typeof overrideStartTime !== 'undefined' ? overrideStartTime : null,
          custom_payload: customPayload,
          inline_payload: customPayload ? { title: customPayload.title || '', ...customPayload } : null,
          creator_id: currentUser?.id || null,
        };

        console.log('🔍 [AddToItineraryModal] 准备插入 row:', row);
        
        const { data: insertedData, error: insertErr } = await supabaseClient
          .from('group_itinerary_entries')
          .insert([row])
          .select()
          .maybeSingle();

        console.log('🔍 [AddToItineraryModal] 插入结果:', { insertedData, insertErr });

        if (insertErr) {
          console.error('Direct insert error', insertErr);
          showError && showError('添加失败：' + (insertErr.message || '未知错误'));
          setLoading(false);
          return;
        }

        const insertedRow = insertedData;
        console.log('🔍 [AddToItineraryModal] insertedRow.id =', insertedRow?.id, 'type:', typeof insertedRow?.id);

        // External share row post-fix: some server logic may default inline_payload/type and ignore p_custom_payload
        if (externalShare) {
          try {
            const currentCustom = insertedRow?.custom_payload;
            const currentInline = insertedRow?.inline_payload;
            const isMissingExternalType = !currentCustom || (typeof currentCustom === 'object' && Object.keys(currentCustom).length === 0) || currentCustom.type !== 'external';
            const inlineMismatch = !currentInline || currentInline.type !== 'external';
            if (isMissingExternalType || inlineMismatch) {
              const patchMetaHost = (() => {
                if (!externalShare?.url) return null;
                try { return new URL(externalShare.url).hostname; } catch { return null; }
              })();
              const patchPayload = customPayload || {
                type: 'external',
                title: externalShare.title || '外部分享',
                description: externalShare.text || null,
                website: externalShare.url || null,
                imageUrl: externalShare.imageUrl || null,
                siteName: patchMetaHost,
                favicon: fallbackFaviconUrl(null, externalShare.url || null),
                partner: detectExternalPartner(externalShare.url || null, patchMetaHost),
                resolvedFinalUrl: null,
                degraded: null,
                source: { kind: 'web-share', via: externalShare.source || 'manual', at: new Date().toISOString() },
              };
              console.debug('[AddToItineraryModal] external patch attempt', { insertedId: insertedRow?.id, isMissingExternalType, inlineMismatch, patchPayload });
              const { error: patchErr, data: patched } = await supabaseClient
                .from('group_itinerary_entries')
                .update({
                  item_type: 'custom',
                  custom_payload: patchPayload,
                  inline_payload: { title: patchPayload.title, type: 'external', ...patchPayload },
                })
                .eq('id', insertedRow.id)
                .select()
                .maybeSingle();
              if (!patchErr && patched) {
                Object.assign(insertedRow, patched);
                console.debug('[AddToItineraryModal] external share row patched successfully');
              } else if (patchErr) {
                console.warn('[AddToItineraryModal] external share patch failed', patchErr);
                // Optimistically fill local object so UI renders even if RLS blocked server patch
                insertedRow.custom_payload = patchPayload;
                insertedRow.inline_payload = { title: patchPayload.title, type: 'external', ...patchPayload };
              }
            }
          } catch (e) {
            console.warn('[AddToItineraryModal] post-persist external patch unexpected error', e);
            if (customPayload) {
              insertedRow.custom_payload = customPayload;
              insertedRow.inline_payload = { title: customPayload.title, type: 'external', ...customPayload };
            }
          }
        }

        showSuccess && showSuccess('已成功添加到行程');
        onAdded && onAdded(insertedRow);
        onClose();
      } catch (e) {
        console.error('RPC call failed', e);
        showError && showError('添加过程中出现错误');
      }
    } catch (e) {
      console.error('handleConfirm error', e);
      showError && showError('添加过程中出现错误');
    } finally {
      setLoading(false);
    }
  };

  if (!visible) return null;

  return (
    <SafeView style={styles.overlayBottom as any} onClick={(e: any) => { if (e && e.target && e.target === e.currentTarget) onClose(); }}>
      <View style={[styles.sheetBottom as any, isExpanded && styles.sheetFull]} onClick={(e: any) => e.stopPropagation()}>
        {/* top handle */}
        <View style={styles.handleRow}>
          <TouchableOpacity
            style={styles.handle}
            activeOpacity={0.7}
            onPointerDown={(e: any) => startDrag(e.clientY)}
          />
          {isExpanded ? (
            <TouchableOpacity onPress={onClose} style={styles.closeBtn}>
              <Text style={{fontSize:18}}>✕</Text>
            </TouchableOpacity>
          ) : null}
        </View>
        <Text style={styles.title}>添加到行程</Text>

        {/* 外部分享预览 */}
        {externalShare && (externalShare.title || externalShare.text || externalShare.url) ? (
          <View style={{ borderWidth: 1, borderColor: '#E5E7EB', backgroundColor: '#FFFFFF', borderRadius: 12, padding: 12, marginBottom: 10 }}>
            <Text style={{ fontSize: 14, fontWeight: '700', color: '#0F172A', marginBottom: 6 }}>收到的分享内容</Text>
            {externalShare.title ? (<Text style={{ fontSize: 14, fontWeight: '600', color: '#0E7490' }}>{externalShare.title}</Text>) : null}
            {externalShare.text ? (<Text style={{ marginTop: 6, fontSize: 13, color: '#374151' }}>{externalShare.text}</Text>) : null}
            {externalShare.url ? (
              <a href={externalShare.url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', marginTop: 8, fontSize: 13, fontWeight: 600, color: '#0369A1', textDecoration: 'none' }}>预览链接</a>
            ) : null}
          </View>
        ) : null}
        <View style={{ marginBottom: 8 }}>
    <Text style={styles.label}>选择已有行程</Text>
          {groupsLoading ? (
            <View style={{ padding: 16, alignItems: 'center', justifyContent: 'center' }}>
              <ActivityIndicator size="small" color="#0ea5a4" />
              <Text style={{ marginTop: 12, color: '#6B7280' }}>正在加载行程...</Text>
            </View>
          ) : groups.length === 0 ? (
            <View style={{ padding: 12 }}>
              <Text>你还没有行程，可前往创建行程页面创建一个完整行程。</Text>
              <View style={{ marginTop: 8, flexDirection: 'row', justifyContent: 'flex-end' }}>
                <TouchableOpacity style={styles.cancelBtn} onPress={() => { onClose(); navigate('/travel/create'); }}>
                  <Text style={styles.cancelText}>去创建行程</Text>
                </TouchableOpacity>
              </View>
            </View>
          ) : (
            groups.map((g) => {
              const handleRowPress = async () => {
                try {
                  const { data: gdet } = await supabaseClient.from('groups').select('start_date,end_date').eq('id', g.id).single();
                  const start = gdet?.start_date ? new Date(gdet.start_date) : null;
                  const end = gdet?.end_date ? new Date(gdet.end_date) : null;
                  if (!start || !end) {
                    onClose();
                    navigate(`/trip/${g.id}`);
                    return;
                  }
                  start.setHours(0, 0, 0, 0);
                  end.setHours(0, 0, 0, 0);
                  const days: Array<{ dayNumber: number; date: string }> = [];
                  const diffMs = end.getTime() - start.getTime();
                  const diffDays = Math.floor(diffMs / (24 * 3600 * 1000));
                  const total = diffDays >= 0 ? diffDays + 1 : 1;
                  for (let i = 0; i < total; i++) {
                    const d = new Date(start);
                    d.setDate(start.getDate() + i);
                    days.push({ dayNumber: i + 1, date: formatYMD(d) });
                  }
                  if (!days.length) {
                    onClose();
                    navigate(`/trip/${g.id}`);
                    return;
                  }
                  setSelectedGroupId(g.id);
                  setDatePickerDays(days);
                  setDatePickerGroupId(g.id);
                  setDatePickerVisible(true);
                } catch (e) {
                  console.error('open date picker error', e);
                  setSelectedGroupId(g.id);
                }
              };

              return (
                <TouchableOpacity
                  key={g.id}
                  style={[styles.groupRow, selectedGroupId === g.id && styles.groupRowActive]}
                  activeOpacity={0.85}
                  onPress={handleRowPress}
                >
                  <View style={styles.groupLeft}>
                    {(() => {
                      const src = resolveTravelBackgroundSource(g as any) as any;
                      if (src) return <Image source={src} style={styles.groupThumb} />;
                      return <View style={styles.groupThumbPlaceholder} />;
                    })()}
                    <View style={{ flex: 1, marginLeft: 10 }}>
                      <Text style={styles.groupName}>{g.name}</Text>
                      <Text style={styles.groupMeta}>{g.start_date || ''}</Text>
                    </View>
                  </View>
                </TouchableOpacity>
              );
            })
          )}
        </View>

        {/* inline day buttons removed — date selection is handled by the DatePickerModal launched from each group's + */}

        {/* actions removed: additions are performed via each group's right-side + (date picker -> add) */}
        {/* date picker small modal */}
        <DatePickerModal visible={datePickerVisible} days={datePickerDays} onClose={() => setDatePickerVisible(false)} onSelect={async (date, dayNumber) => {
          // when user selects date from small modal, perform immediate add and close
          try {
            // update local selection state for UI
                    setSelectedDate(date);
                    const idx = datePickerDays.findIndex(d => d.date === date);
                    setSelectedDayIndex(idx);
                    // close picker UI
                    setDatePickerVisible(false);
                    // open time picker for this date (allow user to choose or skip)
                    setTimePickerInitial(null); // default
                    setTimePickerForDate({ date, dayNumber, groupId: datePickerGroupId });
                    setTimeSelection('09:00');
                    setTimePickerVisible(true);
          } catch (e) {
            console.error('date select add error', e);
          }
        }} />

        {/* Web-style time picker modal (reuse same simple UI as ItineraryEditor) */}
        {timePickerVisible && timePickerForDate && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000
          }}>
            <div style={{ backgroundColor: '#fff', borderRadius: 12, padding: 20, maxWidth: 420, width: '92%' }}>
              <h3 style={{ marginTop: 0, marginBottom: 12, fontSize: 18, fontWeight: 700 }}>選擇時間 - {cardSnapshot?.title || ''}</h3>
              <input type="time" value={timeSelection} onChange={(e: any) => setTimeSelection(e.target.value)} style={{ width: '100%', padding: 10, fontSize: 16, border: '1px solid #ddd', borderRadius: 6, marginBottom: 16, boxSizing: 'border-box' }} />
              <div style={{ display: 'flex', gap: 12 }}>
                <button onClick={async () => {
                  // cancel: close time picker and do not add
                  setTimePickerVisible(false);
                  setTimePickerForDate(null);
                }} style={{ flex: 1, padding: '10px 16px', backgroundColor: '#f0f0f0', border: 'none', borderRadius: 6, fontSize: 16, cursor: 'pointer' }}>取消</button>
                <button onClick={async () => {
                  // confirm: call handleConfirm with chosen time
                  const t = timePickerForDate;
                  setTimePickerVisible(false);
                  setTimePickerForDate(null);
                  try {
                    await handleConfirm(t?.groupId ?? null, t?.date ?? null, (timeSelection && timeSelection.length === 5) ? timeSelection : null);
                  } catch (e) { console.error('time confirm error', e); }
                }} style={{ flex: 1, padding: '10px 16px', backgroundColor: '#FB7A4A', color: '#fff', border: 'none', borderRadius: 6, fontSize: 16, fontWeight: 700, cursor: 'pointer' }}>確認</button>
              </div>
            </div>
          </div>
        )}
      </View>
    </SafeView>
  );
};

const styles = StyleSheet.create({
  overlayBottom: {
    position: 'fixed',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.36)',
    display: 'flex',
    alignItems: 'center',
    // anchor sheet to bottom so it sits above bottom nav
    justifyContent: 'flex-end',
    zIndex: 9999,
  },
  sheetBottom: {
    width: '100%',
    maxWidth: 720,
    backgroundColor: '#fff',
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    boxShadow: '0 -12px 40px rgba(0,0,0,0.18)',
    padding: 12,
    boxSizing: 'border-box',
    // leave room for bottom nav (approx 64px)
    maxHeight: 'calc(100vh - 64px)',
    // sit above bottom nav
    marginBottom: 64,
    overflow: 'auto'
  },
  sheetFull: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    height: '100vh',
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    paddingTop: 18,
    maxWidth: '100%',
    zIndex: 10000,
  },
  handleRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', position: 'relative' },
  handle: { width: 36, height: 6, borderRadius: 4, backgroundColor: '#E5E7EB', marginBottom: 8 },
  closeBtn: { position: 'absolute', right: 8, top: -4, padding: 8 },
  groupLeft: { flexDirection: 'row', alignItems: 'center', flex: 1 },
  groupThumb: { width: 96, height: 54, borderRadius: 8, backgroundColor: '#eee' },
  groupThumbPlaceholder: { width: 96, height: 54, borderRadius: 8, backgroundColor: '#f3f4f6' },
  title: { fontSize: 18, fontWeight: '700', marginBottom: 8 },
  label: { color: '#6B7280', fontSize: 13, marginBottom: 6 },
  groupRow: { position: 'relative', overflow: 'hidden', paddingVertical: 8, paddingHorizontal: 10, borderRadius: 8, backgroundColor: '#fafafa', marginBottom: 6 },
  groupRowActive: { backgroundColor: '#eef6ff', borderColor: '#cfe6ff', borderWidth: 1 },
  groupName: { fontSize: 15, fontWeight: '600' },
  groupMeta: { fontSize: 12, color: '#9CA3AF' },
  actions: { flexDirection: 'row', justifyContent: 'flex-end', gap: 8, marginTop: 12 },
  cancelBtn: { paddingHorizontal: 12, paddingVertical: 8, borderRadius: 8, backgroundColor: '#fff', borderWidth: 1, borderColor: '#E5E7EB' },
  cancelText: { color: '#374151', fontWeight: '600' },
  confirmBtn: { paddingHorizontal: 16, paddingVertical: 10, borderRadius: 8, backgroundColor: '#0ea5a4' },
  confirmText: { color: '#fff', fontWeight: '700' },
  dayBtn: { paddingVertical: 8, paddingHorizontal: 10, borderRadius: 8, backgroundColor: '#fafafa', marginRight: 8, marginBottom: 8, minWidth: 96, alignItems: 'center' },
  dayBtnActive: { backgroundColor: '#e6f7f4', borderWidth: 1, borderColor: '#a7e1da' },
});

export default AddToItineraryModal;
 
